/**
 * Client-side feature-gate mirror. The server at /api/me/permissions is the
 * enforcement boundary — this is purely for rendering (hide-vs-lock, upsell
 * CTAs). Keep the tier → feature map aligned with services/api/app/permissions/features.py.
 */
export type Tier = 'free' | 'starter' | 'growth' | 'scale' | 'enterprise';

export const TIER_ORDER: Tier[] = ['free', 'starter', 'growth', 'scale', 'enterprise'];

export interface PermissionsResponse {
  tier: Tier;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  features: string[];
  caps: {
    specs: number; // -1 = unlimited
    sdk_repos: number;
    docs_domains: number;
    monitored_prs_per_month: number;
  };
  usage: {
    specs: number;
    sdk_repos: number;
    docs_domains: number;
    monitored_prs_this_period: number;
  };
}

export function hasFeature(perms: PermissionsResponse | null | undefined, f: string): boolean {
  return !!perms?.features.includes(f);
}

export function capReached(
  perms: PermissionsResponse | null | undefined,
  resource: keyof PermissionsResponse['caps'],
): boolean {
  if (!perms) return false;
  const limit = perms.caps[resource];
  if (limit === -1) return false;
  const used = perms.usage[resource as keyof PermissionsResponse['usage']] ?? 0;
  return used >= limit;
}

export function tierRank(t: Tier): number {
  return TIER_ORDER.indexOf(t);
}

export function formatLimit(n: number): string {
  return n === -1 ? 'Unlimited' : n.toLocaleString();
}
